import {
  ShieldCheck,
  ShieldAlert,
  Eye,
  Wrench,
  Search,
  XOctagon,
  Hand,
  Activity,
} from 'lucide-react';

/**
 * DriverStatusBadge — single source of truth for the customer-facing
 * micro-copy mapping for a driver. Mirrors driver_label() in
 * /app/backend/services/drivers_catalog.py.
 *
 * Inputs are loose so this works for both /api/drivers/public output
 * (where the backend already provides `customer_label`) and ad-hoc
 * preview/PHV inputs.
 *
 * Priority order:
 *   1. installer_locked / requires_installer  → "Installateur nodig"
 *   2. not_supported / unknown integration    → "Nog niet ondersteund"
 *   3. research_required                      → "Compatibiliteit nog te controleren"
 *   4. advice_only                            → "Alleen advies, geen automatische sturing"
 *   5. manual_confirmation                    → "Kan na toestemming beperkt schakelen"
 *   6. limited_control                        → "Kan beperkt worden gekoppeld"
 *   7. data_capabilities only                 → "Kan meetdata uitlezen"
 *   8. manual integration                     → "Handmatige route beschikbaar"
 *   9. fallback                               → "Handmatige route beschikbaar"
 */
export function getDriverLabel(d) {
  if (!d) return 'Handmatige route beschikbaar';
  // If backend already computed it, trust it.
  if (d.customer_label) return d.customer_label;
  if (d.label) return d.label;

  const cl = d.control_level || 'none';
  const cs = d.connection_status || 'research_required';
  const it = d.integration_type || 'unknown';
  const hasData = (d.data_capabilities || []).length > 0;
  const hasCtrl = (d.control_capabilities || []).length > 0 &&
    cl !== 'none' && cl !== 'advice_only';

  if (cl === 'installer_locked' || d.requires_installer) return 'Installateur nodig';
  if (cs === 'not_supported') return 'Nog niet ondersteund';
  if (cs === 'research_required' || it === 'unknown') return 'Compatibiliteit nog te controleren';
  if (cl === 'advice_only') return 'Alleen advies, geen automatische sturing';
  if (cl === 'manual_confirmation') return 'Kan na toestemming beperkt schakelen';
  if (cl === 'limited_control') return 'Kan beperkt worden gekoppeld';
  if (hasData && !hasCtrl) return 'Kan meetdata uitlezen';
  if (it === 'manual') return 'Handmatige route beschikbaar';
  return 'Handmatige route beschikbaar';
}

const STYLES = {
  installer:   { cls: 'bg-amber-50 text-amber-800 border-amber-200',   Icon: Wrench },
  unsupported: { cls: 'bg-slate-100 text-slate-700 border-slate-200',  Icon: XOctagon },
  research:    { cls: 'bg-sky-50 text-sky-800 border-sky-200',         Icon: Search },
  advice:      { cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: ShieldCheck },
  consent:     { cls: 'bg-indigo-50 text-indigo-800 border-indigo-200',Icon: Hand },
  limited:     { cls: 'bg-violet-50 text-violet-800 border-violet-200',Icon: Activity },
  data:        { cls: 'bg-teal-50 text-teal-800 border-teal-200',      Icon: Eye },
  manual:      { cls: 'bg-zinc-100 text-zinc-700 border-zinc-200',     Icon: Hand },
  risky:       { cls: 'bg-rose-50 text-rose-800 border-rose-200',      Icon: ShieldAlert },
};

function pickStyle(d) {
  if (!d) return STYLES.manual;
  if (d.safety_status === 'risky_do_not_enable') return STYLES.risky;
  const cl = d.control_level || 'none';
  const cs = d.connection_status || 'research_required';
  if (cl === 'installer_locked' || d.requires_installer) return STYLES.installer;
  if (cs === 'not_supported') return STYLES.unsupported;
  if (cs === 'research_required' || (d.integration_type || 'unknown') === 'unknown') return STYLES.research;
  if (cl === 'advice_only') return STYLES.advice;
  if (cl === 'manual_confirmation') return STYLES.consent;
  if (cl === 'limited_control') return STYLES.limited;
  if ((d.data_capabilities || []).length > 0) return STYLES.data;
  return STYLES.manual;
}

export default function DriverStatusBadge({ driver, size = 'sm', className = '' }) {
  const label = getDriverLabel(driver);
  const { cls, Icon } = pickStyle(driver);
  const sizeCls = size === 'lg' ? 'text-sm px-3 py-1.5' : 'text-xs px-2 py-1';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border ${cls} ${sizeCls} ${className}`}
      data-testid="driver-status-badge"
      title={driver?.limitations || label}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}
